import { verifyProxCard, handleReaderDisconnected as handleReaderDisconnectedAction } from '../actions/proxCard';

/**
 * ProxListenerManager - Singleton service managing all window.impr.prox API lifecycle
 *
 * Centralizes proximity card reader interactions:
 * - Reader availability detection
 * - Unified subscription (badge taps + reader disconnect events)
 * - Tab visibility pause/resume
 * - Cleanup on unload
 *
 * API v3: subscribe() is silent and must explicitly query for initial state
 * - Query isReaderAvailable() first; only add listeners and call
 *   window.impr.prox.subscribe() once the reader is confirmed available
 * - If the reader isn't available (or the query errors), treat it as a
 *   disconnect so the auth flow falls back to another modality
 * - Background tab restrictions: subscribe/query only work from active tabs
 */
class ProxListenerManager {
  constructor(store) {
    if (ProxListenerManager.instance) {
      return ProxListenerManager.instance;
    }

    this.store = store;
    this.isSubscribed = false;
    this.pausedDueToVisibility = false;
    this.sawPresenceEvent = false; // API v3: set on reader-disconnect events

    // Bind event handlers
    this.handleBadgeTap = this.handleBadgeTap.bind(this);
    this.handleReaderDisconnected = this.handleReaderDisconnected.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);

    // Setup visibility change listener
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    ProxListenerManager.instance = this;
  }

  static getInstance(store) {
    if (!ProxListenerManager.instance) {
      ProxListenerManager.instance = new ProxListenerManager(store);
    }
    return ProxListenerManager.instance;
  }

  // Test utility to reset singleton
  static resetInstance() {
    if (ProxListenerManager.instance) {
      ProxListenerManager.instance.cleanup();
    }
    ProxListenerManager.instance = null;
  }

  /**
   * Check if proximity card reader is available
   * @returns {Promise<boolean>} Resolves to true if reader is connected
   */
  async init() {
    if (!this._isProxAPIAvailable() ) {
      return false;
    }

    // isReaderAvailable() can hang forever - race against timeout
    const TIMEOUT_MS = 3000;
    try {
      let timeoutId;
      const available = await Promise.race([
        window.impr.prox.isReaderAvailable(),
        new Promise( (resolve) => {
          timeoutId = setTimeout( () => resolve(false), TIMEOUT_MS);
        }),
      ]);
      clearTimeout(timeoutId); // Clear timeout if prox API resolves first

      if (available === true) {
        // Setup event listeners before subscribing
        document.addEventListener('on_prox_badge_tap', this.handleBadgeTap);
        document.addEventListener('on_prox_reader_disconnected', this.handleReaderDisconnected);

        window.impr.prox.subscribe();
        this.isSubscribed = true;
      }
      return available;
    }
    catch (error) {
      // Treat errors (malformed response, dispatch failure) as unavailable
      return false;
    }
  }

  /**
   * Subscribe to proximity events (badge taps + reader disconnect)
   * API v3: queries isReaderAvailable() first and only subscribes if the
   * reader is present; otherwise treats it as a disconnect
   * Returns Promise that resolves after the availability check completes
   */
  async subscribe() {
    if (!this._isProxAPIAvailable() || this.isSubscribed
      || this.store.getState().login.proxReaderConnected !== true) {
      return;
    }

    // Reset presence event flag
    this.sawPresenceEvent = false;

    // API v3: Must explicitly query for initial reader state before subscribing
    try {
      const TIMEOUT_MS = 3000;
      let timeoutId;
      const available = await Promise.race([
        window.impr.prox.isReaderAvailable(),
        new Promise( (resolve) => {
          timeoutId = setTimeout( () => resolve(false), TIMEOUT_MS);
        }),
      ]);
      clearTimeout(timeoutId); // Clear timeout if prox API resolves first

      if (available === true) {
        // Setup event listeners before subscribing
        document.addEventListener('on_prox_badge_tap', this.handleBadgeTap);
        document.addEventListener('on_prox_reader_disconnected', this.handleReaderDisconnected);

        window.impr.prox.subscribe();
        this.isSubscribed = true;
      }
      else {
        this.store.dispatch(handleReaderDisconnectedAction() );
      }
    }
    catch (error) {
      // On error, assume the reader is unavailable
      this.store.dispatch(handleReaderDisconnectedAction() );
    }
  }

  /**
   * Unsubscribe from proximity events
   * API v2: Single unsubscribe stops both badge taps and reader presence
   */
  unsubscribe() {
    if (!this._isProxAPIAvailable() || !this.isSubscribed) {
      return;
    }

    // Unsubscribe from all proximity events
    window.impr.prox.unsubscribe();
    this.isSubscribed = false;

    // Remove event listeners
    document.removeEventListener('on_prox_badge_tap', this.handleBadgeTap);
    document.removeEventListener('on_prox_reader_disconnected', this.handleReaderDisconnected);
  }

  /**
   * Handle badge tap event
   * @param {CustomEvent} event - Badge tap event containing card data
   */
  handleBadgeTap(event) {
    const currentRoute = document.location.pathname;
    if (currentRoute === '/proxcard') {
      const cardData = event.detail || {};

      // Validate event payload
      const cardId = cardData?.proxDevice?.cardId || '';

      // Immediately unsubscribe
      this.unsubscribe();

      // Trigger proximity card verification
      // This calls verifyProxCard which encrypts and sends credentials to server
      this.store.dispatch(verifyProxCard(cardId) );
    }
  }

  /**
   * Handle reader disconnection event
   * API v3: on_prox_reader_disconnected (change events only)
   */
  handleReaderDisconnected() {
    // API v3: Track that we saw a presence event
    this.sawPresenceEvent = true;

    // Unsubscribe from all proximity events
    this.unsubscribe();

    // Dispatch reader disconnected action (aliased import to avoid name collision)
    this.store.dispatch(handleReaderDisconnectedAction() );
  }

  /**
   * Handle tab visibility change
   * Pause listening when tab becomes inactive, resume when active
   * API v3: subscribe() and isReaderAvailable() don't work from hidden tabs
   */
  handleVisibilityChange() {
    if (document.hidden) {
      // Tab became inactive
      // API v3: unsubscribe() works from hidden tabs (explicitly exempted)
      if (this.isSubscribed) {
        this.unsubscribe();
        this.pausedDueToVisibility = true;
      }
    }
    else {
      // Tab became active
      if (this.pausedDueToVisibility) {

        // Resume regardless of the current route, so a reader that was
        // disconnected while the tab was hidden is detected even if the
        // user has since navigated away from /proxcard
        // API v3: Now that tab is active, subscribe() and isReaderAvailable() will work
        this._checkReaderAndResume().catch(error => {
          console.error('[ProxListenerManager] handleVisibilityChange - error resuming subscription:', error);
        });
        this.pausedDueToVisibility = false;
      }
    }
  }

  /**
   * Helper to resume subscription after tab becomes active
   * API v3: subscribe() calls isReaderAvailable() internally for initial state
   * @private
   */
  async _checkReaderAndResume() {
    if (this._isProxAPIAvailable() ) {
      // API v3: subscribe() will handle querying initial state
      await this.subscribe();
    }
    else {
      this.store.dispatch(handleReaderDisconnectedAction() );
    }
  }

  /**
   * Cleanup all listeners and state
   * API v2: Single unsubscribe handles everything
   */
  cleanup() {
    this.unsubscribe();

    // Remove visibility change listener
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);

    // Reset visibility-pause state
    this.pausedDueToVisibility = false;
  }

  /**
   * Check if prox API is available
   * @returns {boolean}
   * @private
   */
  _isProxAPIAvailable() {
    return !!(window.impr && window.impr.prox);
  }
}

// Singleton instance
ProxListenerManager.instance = null;

export default ProxListenerManager;
